import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { formatZAR } from "@/lib/api";
import { MODIFIER } from "@/constants/testIds";
import { Plus, Minus } from "lucide-react";
import { toast } from "sonner";

export default function ModifierModal({ open, onOpenChange, item, onAdd }) {
    const [selections, setSelections] = useState({}); // { groupName: [{name, price}, ...] }
    const [qty, setQty] = useState(1);

    React.useEffect(() => {
        if (open) {
            setSelections({});
            setQty(1);
        }
    }, [open]);

    if (!item) return null;

    const modifiers = item.modifiers || [];

    const toggle = (group, option) => {
        setSelections((prev) => {
            const list = prev[group.name] || [];
            if (group.multi) {
                const exists = list.find((o) => o.name === option.name);
                return {
                    ...prev,
                    [group.name]: exists
                        ? list.filter((o) => o.name !== option.name)
                        : [...list, { group: group.name, name: option.name, price: option.price }],
                };
            }
            return { ...prev, [group.name]: [{ group: group.name, name: option.name, price: option.price }] };
        });
    };

    const isChosen = (group, opt) => {
        const list = selections[group.name] || [];
        return !!list.find((o) => o.name === opt.name);
    };

    const missingRequired = modifiers
        .filter((g) => g.required)
        .filter((g) => !(selections[g.name] && selections[g.name].length > 0));

    const chosenMods = Object.values(selections).flat();
    const modsTotal = chosenMods.reduce((s, m) => s + Number(m.price || 0), 0);
    const lineUnit = Number(item.price) + modsTotal;

    const handleAdd = () => {
        if (missingRequired.length > 0) {
            toast.error(`Please choose: ${missingRequired.map((g) => g.name).join(", ")}`);
            return;
        }
        onAdd({
            item_id: item.id,
            name: item.name,
            price: Number(item.price),
            quantity: qty,
            modifiers: chosenMods,
            line_total: lineUnit * qty,
        });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={MODIFIER.dialog}
                className="max-w-lg bg-brand-charcoal border border-brand-line text-brand-cream rounded-sm"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl">{item.name}</DialogTitle>
                    <p className="text-sm text-brand-mute">{item.description}</p>
                </DialogHeader>

                <div className="max-h-[50vh] overflow-y-auto space-y-6 pr-1">
                    {modifiers.map((group) => (
                        <div key={group.name}>
                            <p className="text-sm tracking-wide text-brand-gold uppercase mb-3">
                                {group.name}
                                {group.required && <span className="text-brand-mute normal-case"> · required</span>}
                            </p>
                            <div className="space-y-2">
                                {group.options.map((opt) => {
                                    const testId = `${MODIFIER.optionPrefix}${group.name}-${opt.name}`.toLowerCase().replace(/\s+/g, "-");
                                    return (
                                        <label
                                            key={opt.name}
                                            data-testid={testId}
                                            className={`flex items-center justify-between gap-3 border px-3 py-2 rounded-sm cursor-pointer transition-colors ${
                                                isChosen(group, opt)
                                                    ? "border-brand-olive bg-brand-olive/10"
                                                    : "border-brand-line hover:border-brand-gold/50"
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                {group.multi ? (
                                                    <Checkbox
                                                        checked={isChosen(group, opt)}
                                                        onCheckedChange={() => toggle(group, opt)}
                                                        className="border-brand-line data-[state=checked]:bg-brand-olive"
                                                    />
                                                ) : (
                                                    <input
                                                        type="radio"
                                                        checked={isChosen(group, opt)}
                                                        onChange={() => toggle(group, opt)}
                                                        className="accent-brand-olive"
                                                    />
                                                )}
                                                <span className="text-sm">{opt.name}</span>
                                            </div>
                                            <span className="text-sm text-brand-gold">
                                                {opt.price > 0 ? `+${formatZAR(opt.price)}` : "Included"}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <DialogFooter className="flex-row items-center justify-between gap-4 sm:justify-between">
                    <div className="flex items-center gap-2 border border-brand-line rounded-sm">
                        <button className="p-2 hover:text-brand-gold" onClick={() => setQty((q) => Math.max(1, q - 1))}>
                            <Minus size={14} />
                        </button>
                        <span className="w-8 text-center">{qty}</span>
                        <button className="p-2 hover:text-brand-gold" onClick={() => setQty((q) => q + 1)}>
                            <Plus size={14} />
                        </button>
                    </div>
                    <button
                        data-testid={MODIFIER.addBtn}
                        onClick={handleAdd}
                        className="btn-primary flex-1"
                    >
                        Add · {formatZAR(lineUnit * qty)}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
